import type {
	PartnerBankAccountResponseDto,
	PartnerBankAccountUpdateDto,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { getFormValuesDiff } from "Infrastructure/Utilities/Forms/Helpers/getFormValuesDiff";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import { useUpdateBankAccount } from "Partner/Api/Mutation/updatePartnerBankAccount";
import {
	getPartnerBankAccountInfoSchema,
	type PartnerBankAccountFormModel,
} from "Partner/Components/Form/Schema/partnerBankAccountInfoSchema";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Grid2 } from "@mui/material";
import { useForm } from "react-hook-form";

const bankAccountResources = Resources.base.partner.detail.bankAccount;

export type BankAccountProps = {
	partnerBankAccountData?: PartnerBankAccountResponseDto[];
	partnerId: number;
	isFormDisabled?: boolean;
};

export function BankAccountForm({
	partnerBankAccountData,
	partnerId,
	isFormDisabled,
}: BankAccountProps) {
	const { t } = useResource();

	const form = useForm<PartnerBankAccountFormModel>({
		resolver: zodResolver(getPartnerBankAccountInfoSchema()),
		defaultValues: partnerBankAccountData?.[0],
	});

	const {
		control,
		formState: { errors, dirtyFields, isDirty },
	} = form;

	const { mutate: updateBankAccount, isPending } =
		useUpdateBankAccount(partnerId);

	const { handleServerError } = useHandleServerError(form);

	const handleSubmitUpdate = () => {
		handleServerError((data) => {
			const changedValues = getFormValuesDiff(
				dirtyFields,
				data,
			) as PartnerBankAccountUpdateDto;

			if (Object.keys(changedValues).length === 0) {
				return;
			}

			return (
				partnerBankAccountData?.[0].id &&
				updateBankAccount({
					data: changedValues,
					id: partnerBankAccountData[0].id,
				})
			);
		});
	};

	return (
		<>
			<Grid2 container spacing={2}>
				<Grid2 size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
					<Input
						id="iban"
						name="iban"
						label={t(bankAccountResources.iban)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						disabled={isFormDisabled}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
					<Input
						id="bic"
						name="bic"
						label={t(bankAccountResources.bic)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						disabled={isFormDisabled}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
					<Input
						id="account-holder"
						name="accountHolder"
						label={t(bankAccountResources.accountHolder)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						disabled={isFormDisabled}
					/>
				</Grid2>
			</Grid2>
			<Button
				variant="contained"
				color="primary"
				onClick={handleSubmitUpdate}
				disabled={isFormDisabled || !isDirty}
				loading={isPending}
				sx={{ mt: 2 }}
			>
				{t(bankAccountResources.update)}
			</Button>
		</>
	);
}
